import './SensorHistory.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { InputLabel, MenuItem, Select } from '@mui/material';

import isEqual from 'lodash/isEqual';
import PlaceholderChart from 'src/screen/Protected/DashboardScreen/assets/placeholder-chart.png';
import variables from 'src/style/variable/variables.module.scss';
import { fetchSensorHistory } from 'src/module/device/action';
import { getSensorData, getSensorDateLoading } from 'src/module/device/selector';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import LogoSpinner from './UI/LogoSpinner';
import { getKWHTitle } from 'src/utils/statFunctions';
import { Chart } from 'react-chartjs-2';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceRotate from 'src/component/UI/DeviceRotate';
import { useTranslation } from 'src/i18n';

import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
} from 'chart.js';


export default function SensorHistory () {
  const { t } = useTranslation();
  const [timeFrame, setTimeFrame] = React.useState('3-months');

  const filters = useSelector(getDevicesSelectedFiltersObj, isEqual);
  const sensorData = useSelector(getSensorData(timeFrame, 'Monthly'));
  const loading = useSelector(getSensorDateLoading());
  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController
  );

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);

  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);

  const kwhValues = sensorData.get('kwh');

  const maxKwh = sensorData.get('kwh') ? Math.max(...kwhValues) : 0;
  const kwhTitle = getKWHTitle(maxKwh);


  const dispatch = useDispatch();
  const ref_timeFrame = useRef('3-months');
  useEffect(() => {
    if (timeFrame != ref_timeFrame.current) {
      ref_timeFrame.current = timeFrame;
      dispatch(fetchSensorHistory(filters, timeFrame, 'Monthly'));
    }

  }, [timeFrame]);


  const timeFrameChange = (event) => {
    const val = event.target.value as string;
    setTimeFrame(val);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    lineTension: 0.5,
    stacked: false,
    elements: {
      point: {
        radius: 0
      }
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        labels: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          },
        }
      }
    },
    scales: {
      x: {
        title: { text: t('sensorHistory.monthly'), display: true, font: {
          size: 15,
          color: '#000000',
          family: 'Montserrat'
        } },
        grid: {
          drawOnChartArea: false,
          display: false
        },
        ticks: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          text: t('sensorHistory.temperature'), display: true, font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        },
        grid: {
          drawOnChartArea: false,
          display: false
        },
        ticks: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
          display: false
        },
        title: {
          text: kwhTitle, display: true, font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        },
        ticks: {
          font: {
            size: 15,
            color: '#000000',
            family: 'Montserrat'
          }
        }
      },
    },
  };
  const allData = {
    labels: sensorData.get('time')?.map(x => moment(x).format('YYYY-MM-DD')).toJS(),
    temperature: sensorData.get('weather')?.toJS(),
    kwh: sensorData.get('kwh')?.toJS()
  };
  const data = {
    labels: allData.labels,
    datasets: [
      {
        type: 'line' as const,
        label: t('sensorHistory.ambientTemperature'),
        data: allData.temperature,
        borderColor: variables.quaternary,
        backgroundColor: variables.quaternary,
        yAxisID: 'y'
      },
      {
        type: 'bar' as const,
        label: t('sensorHistory.totalKwh', { unit: kwhTitle }),
        data: allData.kwh,
        borderColor: variables.secondary,
        backgroundColor: variables.secondary,
        yAxisID: 'y1',
      }
    ],
  };

  return showRotateMessage ? <DeviceRotate /> : <>
    <div>
      <div className='graph-options'>
        <div>
          <InputLabel >
            {t('common.timeFrame')}
          </InputLabel>
          <Select
            id={'timeFrame'}
            labelId={`timeFrame`}
            name={'timeFrame'}
            label={t('common.timeFrame')}
            value={timeFrame}
            onChange={timeFrameChange}
            style={{ margin: '0px' }}
          >
            <MenuItem value={'3-months'}>{t('sensorHistory.3months')}</MenuItem>
            <MenuItem value={'1-year'}>{t('sensorHistory.1year')}</MenuItem>
            <MenuItem value={'2-years'}>{t('sensorHistory.2years')}</MenuItem>
          </Select>
        </div>

      </div>
      {loading ? <div style={{ width: '300px', height: '500px', margin: 'auto', paddingTop: '200px' }}>
        <LogoSpinner size={200} />
      </div> : null}
    </div>
    {!loading && sensorData?.get('kwh') ? (
      <div style={{ height: '350px', margin: 'auto' }}>
        <Chart type='bar' data={data} options={options} />
      </div>
    ) : null}
    {!loading && !sensorData?.get('kwh') ?


      <div className="placeholder-graph">
        <div className='outer placeholder-graph-overlay'>
          <div className="middle">
            <div className="inner">{t('chart.noHistory')}</div>
          </div>
        </div>
        <img width='100%' src={PlaceholderChart} alt="Placeholder Graph" />
      </div>
      : null
    }
  </>;
}
